<template>
  <div v-if="auditList.length > 0" :class="$style.container">
    <div :class="$style.titleSection">
      <div :class="$style.title">New Responses</div>
    </div>

    <div :class="$style.contentSection">
      <div :class="$style.action">
        <BaseFilterDropdown
          :class="$style.auditFilter"
          :filterTitle="`Audits`"
          :fieldName="`Data Collection Period`"
          :filterList="auditList"
          @applyFilter="applyAuditFilter"
        />
        <div class="field" :class="$style.period">
          <label class="label">Period</label>
          <div class="control">
            <b-datepicker
              v-model="selectedPeriod"
              :max-date="maxDate"
              :first-day-of-week="1"
              placeholder="Click to select..."
              range
              :date-formatter="formatDateRange"
              @input="filterPeriod"
              icon="calendar-today"
              icon-pack="mdi"
              :class="[$style.customCalendarIcon, 'calender-icon']"
            >
            </b-datepicker>
          </div>
        </div>
      </div>

      <div :class="$style.chartContainer">
        <VerticalBarChart
          :key="barChartKey"
          :class="$style.chart"
          :barChartData="chartData"
          :barChartOptions="options"
        ></VerticalBarChart>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { State, Action } from 'vuex-class';
import { isTruthy } from '@/jbi-shared/util/watcher.vue-decorator';
import VerticalBarChart from '@/components/reports/VerticalBarChart';
import BaseFilterDropdown from '@/components/base/BaseFilterDropdown.vue';
import {
  DashboardChartData,
  DashboardChartDataRequestPayload,
} from '@/store/modules/projects/types/projects.types';
import { RootState } from '@/store/store';

/**
 * Buefy range datepicker can briefly set v-model to null/[] during sync.
 * Normalize to [start, end] so chart requests never read undefined[0].
 */
function coerceDashboardPeriod(model: unknown): [Date, Date] {
  if (
    Array.isArray(model) &&
    model.length >= 2 &&
    model[0] instanceof Date &&
    model[1] instanceof Date
  ) {
    return [model[0], model[1]];
  }
  const end = new Date();
  const start = new Date(end.getFullYear(), end.getMonth(), end.getDate() - 7);
  return [start, end];
}

const dirtyMonths = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sept',
  'Oct',
  'Nov',
  'Dec',
];

@Component({
  components: {
    BaseFilterDropdown,
    VerticalBarChart,
  },
})
export default class ProjectDashboardChart extends Vue {
  @Prop({ default: () => [] })
  public auditList!: any[];
  @Action('projects/getDashboardChartData')
  public getDashboardChartData!: (
    payload: DashboardChartDataRequestPayload,
  ) => void;

  @State((state: RootState) => state.projects.dashboardChartData)
  public dashboardChartData!: DashboardChartData[];

  public today = new Date();
  public maxDate = new Date();
  public selectedAuditIds: number[] = [];
  public selectedPeriod: Date[] = coerceDashboardPeriod(null);
  public barChartKey: number = Math.floor(Math.random() * 999);

  public chartData = {
    labels: [],
    datasets: [
      {
        label: 'Total Data Collected',
        data: [],
        backgroundColor: Array(0).fill('#1D445E'),
        barThickness: 60,
      },
    ],
  };

  get projectId(): number {
    return +this.$route.params.projectId;
  }

  public created(): void {
    this.selectedAuditIds = (this.auditList ?? []).map((audit) => audit.id);
  }

  @Watch('auditList', { deep: true })
  public onAuditListChange(list: unknown[]): void {
    this.selectedAuditIds = (list ?? []).map((audit: any) => audit.id);
  }

  public options = {
    scales: {
      yAxes: [
        {
          ticks: {
            beginAtZero: true,
          },
          gridLines: {
            display: true,
          },
          scaleLabel: {
            labelString: 'New Responses',
            display: true,
          },
        },
      ],
      xAxes: [
        {
          gridLines: {
            display: false,
          },
          scaleLabel: {
            labelString: 'Date',
            display: true,
          },
        },
      ],
    },
    legend: {
      display: false,
    },
    responsive: true,
    maintainAspectRatio: false,
  };

  public mounted() {
    this.filterChartData();
  }

  public filterPeriod(newValue: Date[] | null) {
    const next = coerceDashboardPeriod(newValue);
    this.selectedPeriod = [next[0], next[1]];
    this.filterChartData();
  }

  public filterChartData() {
    const [from, to] = coerceDashboardPeriod(this.selectedPeriod);
    this.selectedPeriod = [from, to];
    const payload: DashboardChartDataRequestPayload = {
      projectId: this.projectId,
      fromDate: this.setTimeStart(new Date(from.getTime())),
      toDate: this.setTimeEnd(new Date(to.getTime())),
      auditIds: this.selectedAuditIds,
    };

    this.getDashboardChartData(payload);
  }

  public setTimeEnd(date: Date) {
    date.setHours(23);
    date.setMinutes(59);
    date.setSeconds(59);
    return date;
  }

  public setTimeStart(date: Date) {
    date.setHours(0);
    date.setMinutes(0);
    date.setSeconds(0);
    return date;
  }

  public formatDateRange(date: Date[] | null | undefined) {
    const [a, b] = coerceDashboardPeriod(date);
    return this.getDate(a) + ' - ' + this.getDate(b);
  }

  public getDate(newValue: Date) {
    return (
      newValue.getDate() +
      ' ' +
      dirtyMonths[newValue.getMonth()] +
      ' ' +
      newValue.getFullYear()
    );
  }

  public accumulateNewResponseByDate(
    data: DashboardChartData[],
  ): DashboardChartData[] {
    const accumulatedData: DashboardChartData[] = [];
    data.reduce((prev, curr) => {
      const pureDate: string = new Date(curr.createdDate).toLocaleDateString();
      if (!prev[pureDate]) {
        prev[pureDate] = { pureDate, sum: 0, createdDate: curr.createdDate };
        accumulatedData.push(prev[pureDate]);
      }
      prev[pureDate].sum += +curr.sum;
      return prev;
    }, {} as any);
    return accumulatedData;
  }

  @Watch('dashboardChartData')
  @isTruthy
  public watchDashboardChartData(newValue: DashboardChartData[]) {
    const lables: any = [];
    const data: any = [];

    this.accumulateNewResponseByDate(newValue).forEach(
      (chartData: DashboardChartData) => {
        lables.push(this.getDate(new Date(chartData.createdDate)));
        data.push(chartData.sum);
      },
    );
    this.chartData = {
      labels: lables,
      datasets: [
        {
          label: 'Total Data Collected',
          data,
          backgroundColor: Array(data.length).fill('#1D445E'),
          barThickness: 60,
        },
      ],
    };
    this.barChartKey += 1;
  }

  public applyAuditFilter(newValue: number[]) {
    this.selectedAuditIds = newValue;
    this.filterChartData();
  }
}
</script>

<style lang="scss" module>
.container {
  margin-bottom: 5rem;

  .titleSection {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    .title {
      font-weight: bold;
      text-transform: uppercase;
      padding: 8px 0;
    }
  }

  .contentSection {
    box-shadow: 0 2px 2px $grey-border;
    border: 0.5px solid $grey-border;
    border-radius: 6px;
    background: white;
    // overflow-x: auto;
    padding: 1.5rem;

    .action {
      display: flex;
      .auditFilter {
        margin-right: 15px;
      }

      .period {
        flex-grow: 2;
      }
    }

    .chartContainer {
      display: flex;
      padding-top: 20px;
      min-height: 300px;

      .chart {
        color: #444f51;
        font-size: 16px;
        height: 300px;
        width: 100%;
      }
    }
  }
}

.customCalendarIcon {
  i::before {
    padding-top: 13px;
    padding-bottom: 12px;
    width: 15px;
    margin-right: 10px;
  }
}

.dropdown {
  :global(.dropdown-menu) {
    padding: 0;

    :global(.dropdown-content) {
      padding: 0;

      :global(.dropdown-item) {
        padding: 10px;
      }
    }
  }

  .dropdownButton {
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;

    span {
      font-weight: normal;
    }
  }
}
</style>
